/**
 * PEDIRLE LA UBICACIÓN AL APARATO. Solo eso: no la guarda.
 *
 * Existe porque ahora hay DOS pantallas que la piden —el perfil, que la
 * guarda al toque, y el registro, que la lleva adentro del formulario— y las
 * rarezas de la API de geolocalización son las mismas en las dos:
 *
 *  · HAY NAVEGADORES DONDE NO EXISTE, y páginas servidas sin https donde
 *    tampoco. Decirlo es mejor que un botón que no hace nada.
 *  · EL NAVEGADOR NO DICE POR QUÉ FALLÓ de una forma que valga la pena
 *    traducir —«PERMISSION_DENIED» y poco más—, y el caso de lejos más común
 *    es que la persona dijo que no. Así que se dice eso y qué hacer.
 *  · DIEZ SEGUNDOS DE ESPERA: si el GPS no resolvió en ese rato, adentro de
 *    un gimnasio no va a resolver. `maximumAge` de cinco minutos evita
 *    encender la antena de nuevo por algo que se midió recién.
 *
 * Copiar esto en la segunda pantalla habría sido copiar también la próxima
 * corrección — y solo en una de las dos.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface DeviceLocation {
  latitude: number;
  longitude: number;
}

export function useDeviceLocation(): {
  busy: boolean;
  error: string | null;
  limpiarError: () => void;
  /** Le pide la ubicación al aparato. Llama a `alTener` solo si la dio. */
  pedir: (alTener: (location: DeviceLocation) => void) => void;
} {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function pedir(alTener: (location: DeviceLocation) => void): void {
    setError(null);

    if (typeof navigator === 'undefined' || navigator.geolocation === undefined) {
      setError(t('location.unsupported'));
      return;
    }

    setBusy(true);
    navigator.geolocation.getCurrentPosition(
      (posicion) => {
        setBusy(false);
        alTener({ latitude: posicion.coords.latitude, longitude: posicion.coords.longitude });
      },
      () => {
        setBusy(false);
        setError(t('location.denied'));
      },
      { timeout: 10_000, maximumAge: 300_000 },
    );
  }

  return { busy, error, limpiarError: () => setError(null), pedir };
}
